import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";

const emailSchema = z.object({
  email: z
    .string()
    .min(1, "Email address is required.")
    .email("Please enter a valid email address."),
});

type EmailFormValues = z.infer<typeof emailSchema>;

interface EmailEntryProps {
  onSubmit: (email: string, turnstileToken: string) => void;
  loading: boolean;
  error: string | null;
  successMessage?: string;
  turnstileSiteKey: string;
}

export default function EmailEntry({
  onSubmit,
  loading,
  error,
  successMessage,
  turnstileSiteKey,
}: EmailEntryProps) {
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  // The widget is rendered "flexible" (visible), so the token arrives
  // asynchronously via onSuccess — possibly after an interactive challenge.
  // Track it in state and gate submit on it so we never POST an empty token
  // (the cause of the silent TURNSTILE_FAILED 422s). Mirrors zen-forms.
  const [turnstileToken, setTurnstileToken] = useState("");
  const isTurnstileReady = turnstileToken.length > 0;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
  });

  const onFormSubmit = (data: EmailFormValues) => {
    if (!isTurnstileReady) {
      return;
    }

    onSubmit(data.email, turnstileToken);
    // Turnstile tokens are single-use: clear local state and reset the widget
    // so a subsequent attempt fetches a fresh token.
    setTurnstileToken("");
    turnstileRef.current?.reset();
  };

  return (
    <div>
      <h1 className="h3 fw-bold text-dark mb-1">Sign in</h1>
      <p className="text-muted mb-4">Enter your email address to continue.</p>

      {successMessage && (
        <div className="alert alert-success py-2 mb-3" role="alert">
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
        <div className="mb-3">
          <label htmlFor="email" className="form-label fw-medium">
            Email address
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus
            className={`form-control form-control-lg${
              errors.email ? " is-invalid" : ""
            }`}
            placeholder="you@example.com"
            disabled={loading}
            {...register("email")}
          />
          {errors.email && (
            <div className="invalid-feedback">{errors.email.message}</div>
          )}
        </div>

        {error && (
          <div className="alert alert-danger py-2 mb-3" role="alert">
            {error}
          </div>
        )}

        {turnstileSiteKey && (
          <div className="mb-3 d-flex justify-content-center">
            <Turnstile
              ref={turnstileRef}
              siteKey={turnstileSiteKey}
              options={{ size: "flexible" }}
              onSuccess={(token) => setTurnstileToken(token)}
              onError={() => setTurnstileToken("")}
              onExpire={() => setTurnstileToken("")}
            />
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-lg w-100"
          disabled={loading || (Boolean(turnstileSiteKey) && !isTurnstileReady)}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Checking...
            </>
          ) : (
            "Continue"
          )}
        </button>

        {turnstileSiteKey && !isTurnstileReady && (
          <p className="text-muted text-center small mt-2 mb-0">
            Please complete the security check above before continuing.
          </p>
        )}
      </form>
    </div>
  );
}
